import { FC, useEffect, useState } from 'react';
import { createCheckoutSession, getPlans } from '@/services/subscription.service';
import { Plan } from '@/types/payment';
import { useToast } from '@/contexts/ToastContext';
import { Check, Shield, Zap, CreditCard, ArrowRight } from 'lucide-react';
import { Modal, ModalFooter } from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/utils/error';

interface PlanSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPaymentSuccess?: () => void;
}

const PlanSelectionModal: FC<PlanSelectionModalProps> = ({
    isOpen,
    onClose,
    onPaymentSuccess,
}) => {
    const { addToast } = useToast();
    const [plans, setPlans] = useState<Plan[]>([]);
    const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [fetchingPlans, setFetchingPlans] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchPlans();
        }
    }, [isOpen]);

    const fetchPlans = async () => {
        setFetchingPlans(true);
        try {
            const data = await getPlans();
            setPlans(data);
            if (data.length > 0) {
                // Pre-select first plan or active one if possible
                setSelectedPlanId(data[0].id);
            }
        } catch (err) {
            console.error('Failed to fetch plans:', err);
            addToast('Failed to load plans.', 'error');
        } finally {
            setFetchingPlans(false);
        }
    };

    const handleSubscribe = async () => {
        if (!selectedPlanId) {
            addToast('Please select a plan to continue.', 'error');
            return;
        }

        setLoading(true);
        try {
            const result = await createCheckoutSession(selectedPlanId);

            if (result.manualMode) {
                addToast(result.message || 'Invoice generated successfully.', 'success');
                onPaymentSuccess?.();
                onClose();
                window.location.href = '/dashboard';
                return;
            }

            if (result.url) {
                window.location.href = result.url;
            } else {
                addToast('Failed to initiate checkout. Please try again.', 'error');
                setLoading(false);
            }
        } catch (err: unknown) {
            console.error('Checkout error:', err);
            addToast(getErrorMessage(err), 'error');
            setLoading(false); // Only set to false on error, keep true on success for redirect
        }
    };



    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Upgrade Subscription"
            size="lg"
        >
            <div className="space-y-6">
                <div>
                    <p className="text-sm text-slate-500">
                        Choose the plan that fits your business needs. You can upgrade or downgrade at any time.
                    </p>
                </div>

                {fetchingPlans ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Spinner size="lg" />
                        <p className="mt-4 text-sm text-slate-500 font-medium">Fetching plan details...</p>
                    </div>
                ) : plans.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                            <Shield className="w-6 h-6 text-slate-400" />
                        </div>
                        <p className="text-slate-900 font-semibold">No plans available</p>
                        <p className="text-sm text-slate-500 mt-1">Please contact support for custom configuration.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {plans.map((plan) => {
                            const isSelected = selectedPlanId === plan.id;
                            return (
                                <div
                                    key={plan.id}
                                    onClick={() => setSelectedPlanId(plan.id)}
                                    className={`
                                        relative cursor-pointer p-5 rounded-xl border-2 transition-all duration-200
                                        ${isSelected
                                            ? 'border-slate-900 bg-slate-50 ring-4 ring-slate-900/5'
                                            : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50/50'
                                        }
                                    `}
                                >
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="p-2 rounded-lg bg-white border border-slate-100 shadow-sm">
                                            <Zap className={`w-5 h-5 ${isSelected ? 'text-slate-900' : 'text-slate-400'}`} />
                                        </div>
                                        {isSelected && (
                                            <Badge variant="primary" className="bg-slate-900 text-white rounded-md">Selected</Badge>
                                        )}
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-bold text-slate-900">{plan.displayName}</h3>
                                        <div className="mt-2 flex items-baseline gap-1">
                                            <span className="text-2xl font-black text-slate-900">
                                                {new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: plan.currency || 'USD',
                                                    minimumFractionDigits: 0
                                                }).format(plan.price)}
                                            </span>
                                            <span className="text-sm text-slate-500 font-medium">/{plan.interval || 'mo'}</span>
                                        </div>
                                        {plan.description && (
                                            <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                                                {plan.description}
                                            </p>
                                        )}
                                    </div>

                                    <ul className="mt-5 space-y-2.5">
                                        {plan.features?.slice(0, 4).map((feature, i) => (
                                            <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                                                <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="bg-slate-50 rounded-lg p-4 flex items-center gap-4 border border-slate-100">
                    <div className="w-10 h-10 rounded-full bg-slate-900/10 flex items-center justify-center shrink-0">
                        <CreditCard className="w-5 h-5 text-slate-900" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">Secure Payment</p>
                        <p className="text-xs text-slate-500 truncate">PCI-compliant checkout powered by Stripe</p>
                    </div>
                </div>
            </div>

            <ModalFooter>
                <Button variant="secondary" onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    className="gap-2 min-w-[160px]"
                    onClick={handleSubscribe}
                    isLoading={loading}
                    disabled={!selectedPlanId || fetchingPlans}
                >
                    Continue to Payment
                    <ArrowRight className="w-4 h-4" />
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default PlanSelectionModal;
